import {toJS} from 'mobx'
import i18next, {i18n, TOptions} from 'i18next'
import {get, isArray, keys, merge, set, uniq, cloneDeep, isString} from 'lodash'
import {getOrSet} from './helpers'
import {WidgetConfig} from './WidgetConfig'
import {Compiler, FunctionMocks} from './Compiler'

///////////////////////////////////////////////////////////////////////////////

export type TranslatorArgs = {
  debug?: boolean
  config: WidgetConfig
  compiler: Compiler
}

export type TranslatorMappedResource = {
  [key: string]: TranslatorResource
}

export type TranslatorResource = {
  [key: string]: any
}

///////////////////////////////////////////////////////////////////////////////

export class Translator {
  public debug: boolean
  private compiler: Compiler
  private i18n: i18n

  private CONFIG_NAMESPACE = 'config'
  private TRANSLATIONS_NAMESPACE = 'translations'
  private FALLBACK_KEY = 'fallback'

  private customConfig: any = null
  private generatedConfig: any = null

  private loadedLanguages: string[] = []
  private loadedTranslations: TranslatorMappedResource = {}
  private loadedConfig: TranslatorMappedResource = {}

  constructor(args: TranslatorArgs) {
    let {debug, config, compiler} = args
    let {customConfig, generatedConfig} = toJS(config)

    debug = !!debug

    this.debug = debug
    this.compiler = compiler
    this.customConfig = customConfig
    this.generatedConfig = generatedConfig
  }

  loadResource(language: string) {
    if (this.loadedLanguages.indexOf(language) === -1) {
      let resources = require('../config/config.**.yml')[language]
      let customResources = this.customConfig && this.customConfig[language]
      let generatedResources =
        this.generatedConfig && this.generatedConfig[language]

      this.loadedLanguages.push(language)

      let translations = {}
      let customTranslations = {}
      let generatedTranslations = {}
      let config = {}
      let customConfig = {}
      let generatedConfig = {}

      if (resources) {
        translations = resources[this.TRANSLATIONS_NAMESPACE] || {}
        config = resources[this.CONFIG_NAMESPACE] || {}
      }

      if (customResources) {
        customTranslations = customResources[this.TRANSLATIONS_NAMESPACE] || {}
        customConfig = customResources[this.CONFIG_NAMESPACE] || {}
      }

      if (generatedResources) {
        let parsed = this.parseGeneratedConfig(
          generatedResources[this.CONFIG_NAMESPACE] || {},
          generatedResources[this.TRANSLATIONS_NAMESPACE] || {},
        )

        generatedTranslations = parsed.translations
        generatedConfig = parsed.config
      }

      this.loadedTranslations[language] = merge(
        translations,
        customTranslations,
        generatedTranslations,
      ) as TranslatorResource
      this.loadedConfig[language] = {
        ...config,
        ...customConfig,
        ...generatedConfig,
      }

      this.replaceI18NInstance()
    }
  }

  getTranslation(
    language: string,
    key: string | string[],
    translationOptions: TOptions = null,
    mocks: FunctionMocks = null,
  ): string {
    // todo: remove in a future versions to allow array keys to be passed to "i18n.t"
    key = isArray(key) ? key.join('.') : key
    translationOptions = translationOptions || {}

    this.loadResource(language)

    let {defaultValue, ...options} = translationOptions

    options = {...options, lng: language, returnObjects: true}

    if (!this.i18n.exists(key, options)) {
      if (this.debug && defaultValue === undefined) {
        if (isArray(key)) {
          const keys = key.map((k) => `"${language}.${k}"`)
          console.debug(`[tamaro] Missing translation keys: ${keys.join(', ')}`)
        } else {
          console.debug(
            `[tamaro] Missing translation key: "${language}.${key}"`,
          )
        }
      }

      const k = isArray(key) ? key[0] : key

      return defaultValue !== undefined ? defaultValue : `{${language}.${k}}`
    }

    const translation = this.i18n.t(key, options)
    const conditionResult = this.compiler.resolveConditionWithSingleResult(
      translation,
      mocks,
    )

    if (isString(conditionResult)) {
      return this.compiler.interpolate(conditionResult, mocks) as string
    }

    return conditionResult
  }

  getTranslations(language: string): TranslatorResource {
    this.loadResource(language)

    return this.loadedTranslations[language]
  }

  getTranslationsWithFallbacks(language: string): TranslatorResource {
    return this.getResourceWithFallbacks(language, (language: string) =>
      this.getTranslations(language),
    )
  }

  getLoadedTranslations(): TranslatorMappedResource {
    return this.loadedTranslations
  }

  getLoadedTranslationsWithFallbacks(): TranslatorMappedResource {
    let translationsWithFallbacks = {}

    this.loadedLanguages.forEach((language) => {
      translationsWithFallbacks[language] = this.getTranslationsWithFallbacks(
        language,
      )
    })

    return translationsWithFallbacks
  }

  getConfigs(language: string): TranslatorResource {
    this.loadResource(language)

    return this.loadedConfig[language]
  }

  getConfigsWithFallbacks(language: string): TranslatorResource {
    return this.getResourceWithFallbacks(language, (language: string) =>
      this.getConfigs(language),
    )
  }

  getLoadedConfigs(): TranslatorMappedResource {
    return this.loadedConfig
  }

  getLoadedConfigsWithFallbacks(): TranslatorMappedResource {
    let configsWithFallbacks = {}

    this.loadedLanguages.forEach((language) => {
      configsWithFallbacks[language] = this.getConfigsWithFallbacks(language)
    })

    return configsWithFallbacks
  }

  addTranslations(translations: TranslatorMappedResource) {
    keys(translations).forEach((language) => {
      this.loadedTranslations[language] = merge(
        this.getTranslations(language),
        translations[language],
      )
    })

    this.replaceI18NInstance()
  }

  getAvailableLanguages(): string[] {
    let files = {}

    try {
      files = {...files, ...require('../config/**.yml')}
    } catch (err) {}

    let languages = uniq(
      keys(files).map((f) => {
        let parts = f.split('.')
        return parts[parts.length - 1]
      }),
    )

    let customLanguages = keys(this.customConfig)

    languages = uniq([...languages, ...customLanguages])

    return languages
  }

  private getResourceWithFallbacks(
    language: string,
    provider: (language: string) => TranslatorResource,
  ) {
    let resourcesWithFallbacks = {}
    let iterations = 0

    let relevantResources = []

    while (true) {
      iterations++

      if (iterations > 10) {
        console.error('Translation fallback recursion detected')
      }

      let resource = provider(language)
      relevantResources.push(resource)

      let fallback = resource[this.FALLBACK_KEY]

      if (!fallback) {
        break
      }

      language = fallback.toString()
    }

    relevantResources.reverse().forEach((resource) => {
      resourcesWithFallbacks = {...resourcesWithFallbacks, ...resource}
    })

    return resourcesWithFallbacks
  }

  private replaceI18NInstance() {
    let translationsWithFallbacks = this.getLoadedTranslationsWithFallbacks()
    let restructuredTranslations = {}

    keys(translationsWithFallbacks).forEach((language) => {
      restructuredTranslations[language] = {
        default: translationsWithFallbacks[language],
      }
    })

    this.i18n = i18next.createInstance()
    this.i18n.init({
      defaultNS: 'default',
      resources: restructuredTranslations,
      // not needed for react
      interpolation: {escapeValue: false},
    })
  }

  private parseGeneratedConfig(
    generatedConfig: any,
    generatedTranslations: any,
  ): {config: any; translations: any} {
    let config: any = {}
    let translations: any = {}

    try {
      if (!generatedConfig) {
        return {config, translations}
      }

      this.parseGeneratedPurposesConfig(
        generatedConfig,
        generatedTranslations,
        config,
        translations,
      )
      this.parseGeneratedAmountsConfig(
        generatedConfig,
        generatedTranslations,
        config,
        translations,
      )
    } catch (err) {
      console.error(
        'Could not parse generated widget config',
        generatedConfig,
        generatedTranslations,
      )
      console.error(err)
    }

    return {config, translations}
  }

  private parseGeneratedPurposesConfig(
    generatedConfig: any,
    generatedTranslations: any,
    config: any,
    translations: any,
  ) {
    if (!generatedConfig.purposes) {
      return
    }

    generatedConfig.purposes.forEach((purposeConfig) => {
      if (purposeConfig.enabled === false) {
        return
      }

      if (!config.purposes) {
        config.purposes = []
      }

      config.purposes.push(purposeConfig.id)

      if (purposeConfig.default_purpose === true) {
        config.default_purpose = purposeConfig.id
      }

      if (purposeConfig.campaign_id) {
        set(
          config,
          ['purpose_details', purposeConfig.id, 'campaign_id'],
          purposeConfig.campaign_id,
        )
      }

      if (purposeConfig.campaign_subid) {
        set(
          config,
          ['purpose_details', purposeConfig.id, 'campaign_subid'],
          purposeConfig.campaign_subid,
        )
      }

      if (purposeConfig.title) {
        set(translations, ['purposes', purposeConfig.id], purposeConfig.title)
      }
    })
  }

  private parseGeneratedAmountsConfig(
    generatedConfig: any,
    generatedTranslations: any,
    config: any,
    translations: any,
  ) {
    if (!generatedConfig.amounts) {
      return
    }

    keys(generatedConfig.amounts).forEach((currency) => {
      if (generatedConfig.amounts[currency]) {
        getOrSet(config, 'currencies', []).push(currency)
      }

      let onetimeConfig = generatedConfig.amounts[currency].onetime
      let recurringConfig = generatedConfig.amounts[currency].recurring

      if (onetimeConfig) {
        this.parseGeneratedAmountsConfigWithTypeAndInterval(
          config,
          onetimeConfig,
          currency,
          'onetime',
          null,
        )
      }

      if (recurringConfig) {
        keys(recurringConfig).forEach((recurringInterval) => {
          let recurringIntervalConfig = recurringConfig[recurringInterval]

          if (recurringIntervalConfig) {
            getOrSet(config, 'recurring_intervals', []).push({
              if: 'currency() == ' + currency,
              then: recurringInterval,
            })

            const defaultRecurringInterval = !!recurringIntervalConfig[
              'default_recurring_interval'
            ]

            if (defaultRecurringInterval) {
              set(config, 'default_recurring_interval', recurringInterval)
            }

            this.parseGeneratedAmountsConfigWithTypeAndInterval(
              config,
              recurringIntervalConfig,
              currency,
              'recurring',
              recurringInterval,
            )
          }
        })
      }
    })
  }

  private parseGeneratedAmountsConfigWithTypeAndInterval(
    config: any,
    paymentTypeConfig: any,
    currency: string,
    paymentType: string,
    recurringInterval: string,
  ) {
    if (!paymentTypeConfig || !currency || !paymentType) {
      return
    }

    let configCondition =
      'currency() == ' + currency + ' and paymentType() == ' + paymentType

    if (recurringInterval !== null) {
      configCondition += ' and recurringInterval() == ' + recurringInterval
    }

    if (paymentTypeConfig.allow_custom_amount !== null) {
      getOrSet(config, 'allow_custom_amount', []).push({
        if: configCondition,
        then: paymentTypeConfig.allow_custom_amount,
      })
    }

    if (paymentTypeConfig.minimum_custom_amount !== null) {
      getOrSet(config, 'minimum_custom_amount', []).push({
        if: configCondition,
        then: paymentTypeConfig.minimum_custom_amount,
      })
    }

    if (paymentTypeConfig.maximum_custom_amount !== null) {
      getOrSet(config, 'maximum_custom_amount', []).push({
        if: configCondition,
        then: paymentTypeConfig.maximum_custom_amount,
      })
    }

    if (paymentTypeConfig.amounts) {
      paymentTypeConfig.amounts.forEach((amountConfig) => {
        if (amountConfig.enabled === false) {
          return
        }

        getOrSet(config, 'amounts', []).push({
          if: configCondition,
          then: amountConfig.amount,
        })

        if (amountConfig.default_amount === true) {
          getOrSet(config, 'default_amount', []).push({
            if: configCondition,
            then: amountConfig.amount,
          })
        }
      })
    }
  }
}

export function createTranslator(args: TranslatorArgs): Translator {
  return new Translator(args)
}
